import {
  FormEvent,
  InputHTMLAttributes,
  InvalidEvent,
  ReactNode,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputProps,
  LinearProgress,
  Link,
  Typography,
} from '@mui/joy';
import { useTranslation } from 'react-i18next';
import type { MfaMethod, SignInBusy } from './useSignIn';
import { TotpQrCode } from './TotpQrCode';

// --- Building blocks -----------------------------------------------------------------

/** Microsoft's four-tile logo, in its brand colours, as its sign-in branding requires. */
const MicrosoftMark = () => (
  <span className="sign-in__ms-mark" aria-hidden="true">
    <span className="sign-in__ms-tile" />
    <span className="sign-in__ms-tile" />
    <span className="sign-in__ms-tile" />
    <span className="sign-in__ms-tile" />
  </span>
);

/** Google's "G", in its brand colours, as its sign-in branding requires. */
const GoogleMark = () => (
  <svg className="sign-in__google-mark" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

/** Cognito's identity-provider buttons: 2px dark outline. */
const providerSx = { borderWidth: 2, borderColor: 'text.secondary' } as const;

/** Italic placeholders on the sign-in fields, as on Cognito's page. */
const italicPlaceholderSx = {
  '& input::placeholder': { fontStyle: 'italic' },
} as const;

type FieldProps = Omit<InputProps, 'slotProps'> & {
  label: string;
  /** Attributes for the `<input>` itself, e.g. `inputMode` and `pattern`. */
  htmlInput?: Pick<
    InputHTMLAttributes<HTMLInputElement>,
    'inputMode' | 'pattern'
  >;
};

/**
 * A labelled input that reports the browser's own checks (required, email
 * format, pattern) under the field instead of in the browser's pop-up.
 */
const Field = ({ label, htmlInput, onChange, ...input }: FieldProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const messageFor = (field: HTMLInputElement) => {
    const { validity } = field;
    if (validity.valueMissing) return t('This field is required.');
    if (validity.typeMismatch) return t('Enter a valid email address.');
    if (validity.patternMismatch) return t('Enter numbers only.');
    return field.validationMessage;
  };

  const onInvalid = (event: InvalidEvent<HTMLInputElement>) => {
    event.preventDefault(); // no browser pop-up; the message shows below
    const field = event.currentTarget;
    setError(messageFor(field));
    // Cancelling the event also stops the browser moving focus to the first
    // invalid field. (Read `validity` rather than match `:invalid`: matching
    // can re-run the checks and fire this event again.)
    const firstInvalid = Array.from(field.form?.elements ?? []).find(
      (element) =>
        element instanceof HTMLInputElement && !element.validity.valid
    );
    if (firstInvalid === field) field.focus();
  };

  return (
    <FormControl error={Boolean(error)}>
      <FormLabel>{label}</FormLabel>
      <Input
        {...input}
        onChange={(event) => {
          // Once a message shows, keep it current as the user types.
          if (error) {
            setError(
              event.target.validity.valid ? null : messageFor(event.target)
            );
          }
          onChange?.(event);
        }}
        slotProps={{ input: { ...htmlInput, onInvalid } }}
      />
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

const CodeField = ({ label }: { label: string }) => (
  <Field
    label={label}
    name="code"
    required
    autoFocus
    autoComplete="one-time-code"
    htmlInput={{ inputMode: 'numeric', pattern: '[0-9 ]*' }}
  />
);

interface StepFormProps {
  description?: ReactNode;
  submitLabel: string;
  busy: boolean;
  onSubmit: (data: FormData) => void;
  /** Checks the fields before submitting; returns an error to show, if any. */
  validate?: (data: FormData) => string | null;
  onBack?: () => void;
  children?: ReactNode;
}

const StepForm = ({
  description,
  submitLabel,
  busy,
  onSubmit,
  validate,
  onBack,
  children,
}: StepFormProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const problem = validate?.(data) ?? null;
    setError(problem);
    if (!problem) onSubmit(data);
  };

  return (
    <form className="sign-in__form" onSubmit={submit}>
      {description && (
        <Typography level="body-sm" textColor="text.secondary">
          {description}
        </Typography>
      )}
      {children}
      {error && (
        <FormHelperText sx={{ color: 'danger.plainColor' }}>
          {error}
        </FormHelperText>
      )}
      <Button type="submit" loading={busy} fullWidth>
        {submitLabel}
      </Button>
      {onBack && (
        <Link
          component="button"
          type="button"
          level="body-sm"
          onClick={onBack}
          className="sign-in__back"
        >
          {t('Back to sign in')}
        </Link>
      )}
    </form>
  );
};

const text = (data: FormData, name: string) =>
  String(data.get(name) ?? '').trim();

/** The two new-password fields, and the check that they match. */
const NewPasswordFields = () => {
  const { t } = useTranslation();
  return (
    <>
      <Field
        label={t('New password')}
        name="password"
        type="password"
        required
        autoComplete="new-password"
      />
      <Field
        label={t('Confirm new password')}
        name="confirm"
        type="password"
        required
        autoComplete="new-password"
      />
    </>
  );
};

const usePasswordsMatch = () => {
  const { t } = useTranslation();
  return (data: FormData) =>
    data.get('password') === data.get('confirm')
      ? null
      : t('The passwords do not match.');
};

const readable = (secret: string) =>
  secret.match(/.{1,4}/g)?.join(' ') ?? secret;

// --- Steps --------------------------------------------------------------------------------

export interface CredentialsStepProps {
  microsoft: boolean;
  google: boolean;
  busy: SignInBusy;
  onMicrosoft: () => void;
  onGoogle: () => void;
  onPassword: (email: string, password: string) => void;
  onForgotPassword: () => void;
}

export const CredentialsStep = ({
  microsoft,
  google,
  busy,
  onMicrosoft,
  onGoogle,
  onPassword,
  onForgotPassword,
}: CredentialsStepProps) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const locked = busy !== null;
  const providers = microsoft || google;

  return (
    <>
      {providers && (
        <>
          <section className="sign-in__section">
            <Typography level="title-md" component="h2">
              {t('With other accounts')}
            </Typography>
            {microsoft && (
              <Button
                variant="outlined"
                color="neutral"
                fullWidth
                className="sign-in__provider"
                sx={providerSx}
                startDecorator={<MicrosoftMark />}
                loading={busy === 'microsoft'}
                loadingPosition="start"
                aria-busy={busy === 'microsoft'}
                disabled={locked && busy !== 'microsoft'}
                onClick={onMicrosoft}
              >
                {t('Sign in with Microsoft')}
              </Button>
            )}
            {google && (
              <Button
                variant="outlined"
                color="neutral"
                fullWidth
                className="sign-in__provider"
                sx={providerSx}
                startDecorator={<GoogleMark />}
                loading={busy === 'google'}
                loadingPosition="start"
                aria-busy={busy === 'google'}
                disabled={locked && busy !== 'google'}
                onClick={onGoogle}
              >
                {t('Sign in with Google')}
              </Button>
            )}
          </section>
          <Divider className="sign-in__divider">{t('Or')}</Divider>
        </>
      )}

      <section className="sign-in__section">
        {/* With nothing to set it apart from, the form needs no heading;
            Cognito's page says what it is for instead. */}
        {providers ? (
          <Typography level="title-md" component="h2">
            {t('With existing account')}
          </Typography>
        ) : (
          <Typography level="body-sm" textColor="text.secondary">
            {t('Sign in to your account.')}
          </Typography>
        )}
        <form
          className="sign-in__form"
          onSubmit={(event) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            onPassword(text(data, 'email'), String(data.get('password') ?? ''));
          }}
        >
          <Field
            label={t('Email address')}
            name="email"
            type="email"
            required
            autoComplete="username"
            placeholder="name@host.com"
            sx={italicPlaceholderSx}
          />
          <Field
            label={t('Password')}
            name="password"
            type={showPassword ? 'text' : 'password'}
            required
            placeholder={t('Enter password')}
            autoComplete="current-password"
            sx={italicPlaceholderSx}
          />
          <div className="sign-in__row">
            <Checkbox
              size="sm"
              label={t('Show password')}
              checked={showPassword}
              onChange={(event) => setShowPassword(event.target.checked)}
            />
            <Link
              component="button"
              type="button"
              level="body-sm"
              // At the end of the line even when it is alone on it.
              sx={{ marginInlineStart: 'auto' }}
              onClick={onForgotPassword}
            >
              {t('Forgot your password?')}
            </Link>
          </div>
          <Button
            type="submit"
            fullWidth
            loading={busy === 'password'}
            disabled={locked && busy !== 'password'}
          >
            {t('Sign in')}
          </Button>
        </form>
      </section>
    </>
  );
};

export const CompletingStep = () => {
  const { t } = useTranslation();
  return (
    <div className="sign-in__completing" role="status">
      <MicrosoftMark />
      <Typography level="title-md">{t('Signing you in...')}</Typography>
      <Typography level="body-sm" textColor="text.secondary">
        {t('Microsoft has confirmed your account. Opening Consolidocs.')}
      </Typography>
      <LinearProgress size="sm" className="sign-in__progress" />
    </div>
  );
};

interface StepProps {
  busy: boolean;
  onBack: () => void;
}

export const NewPasswordStep = ({
  busy,
  onBack,
  onSubmit,
}: StepProps & { onSubmit: (password: string) => void }) => {
  const { t } = useTranslation();
  const passwordsMatch = usePasswordsMatch();
  return (
    <StepForm
      description={t(
        'You signed in with a temporary password. Choose a new password to continue.'
      )}
      submitLabel={t('Set password and sign in')}
      busy={busy}
      validate={passwordsMatch}
      onSubmit={(data) => onSubmit(String(data.get('password')))}
      onBack={onBack}
    >
      <NewPasswordFields />
    </StepForm>
  );
};

export const MfaCodeStep = ({
  busy,
  onBack,
  method,
  destination,
  onSubmit,
}: StepProps & {
  method: MfaMethod;
  destination?: string;
  onSubmit: (code: string) => void;
}) => {
  const { t } = useTranslation();
  const description =
    method === 'SOFTWARE_TOKEN_MFA'
      ? t('Enter the 6-digit code from your authenticator app.')
      : t('Enter the code we sent to {{destination}}.', {
          destination: destination ?? t('you'),
        });
  return (
    <StepForm
      description={description}
      submitLabel={t('Verify')}
      busy={busy}
      onSubmit={(data) => onSubmit(text(data, 'code').replace(/\s/g, ''))}
      onBack={onBack}
    >
      <CodeField label={t('Code')} />
    </StepForm>
  );
};

export const MfaChoiceStep = ({
  busy,
  onBack,
  methods,
  onChoose,
}: StepProps & {
  methods: MfaMethod[];
  onChoose: (method: MfaMethod) => void;
}) => {
  const { t } = useTranslation();
  const labels: Record<MfaMethod, string> = {
    SOFTWARE_TOKEN_MFA: t('Authenticator app'),
    SMS_MFA: t('Text message'),
    EMAIL_OTP: t('Email'),
  };
  return (
    <div className="sign-in__form">
      <Typography level="body-sm" textColor="text.secondary">
        {t('Choose how to get your sign-in code.')}
      </Typography>
      {methods.map((method) => (
        <Button
          key={method}
          variant="outlined"
          color="neutral"
          fullWidth
          disabled={busy}
          onClick={() => onChoose(method)}
        >
          {labels[method]}
        </Button>
      ))}
      <Link
        component="button"
        type="button"
        level="body-sm"
        onClick={onBack}
        className="sign-in__back"
      >
        {t('Back to sign in')}
      </Link>
    </div>
  );
};

export const MfaSetupStep = ({
  busy,
  onBack,
  secret,
  account,
  onSubmit,
}: StepProps & {
  secret: string;
  account: string;
  onSubmit: (code: string) => void;
}) => {
  const { t } = useTranslation();
  const uri = `otpauth://totp/${encodeURIComponent(
    `Consolidocs:${account}`
  )}?secret=${secret}&issuer=Consolidocs`;
  return (
    <StepForm
      description={t(
        'Your organisation requires a second step at sign-in. Scan this code with an authenticator app, such as Microsoft Authenticator or Google Authenticator, then enter the 6-digit code it shows.'
      )}
      submitLabel={t('Verify and sign in')}
      busy={busy}
      onSubmit={(data) => onSubmit(text(data, 'code').replace(/\s/g, ''))}
      onBack={onBack}
    >
      <TotpQrCode uri={uri} label={t('QR code for your authenticator app')} />
      <Typography level="body-xs" textColor="text.secondary">
        {t("Can't scan it? Enter this key instead:")}{' '}
        <span className="sign-in__secret">{readable(secret)}</span>
      </Typography>
      <CodeField label={t('Code')} />
    </StepForm>
  );
};

export const ForgotPasswordStep = ({
  busy,
  onBack,
  onSubmit,
}: StepProps & { onSubmit: (email: string) => void }) => {
  const { t } = useTranslation();
  return (
    <StepForm
      description={t(
        'Enter your email address and we will send you a code to reset your password.'
      )}
      submitLabel={t('Send code')}
      busy={busy}
      onSubmit={(data) => onSubmit(text(data, 'email'))}
      onBack={onBack}
    >
      <Field
        label={t('Email address')}
        name="email"
        type="email"
        required
        autoFocus
        autoComplete="username"
      />
    </StepForm>
  );
};

export const ResetPasswordStep = ({
  busy,
  onBack,
  destination,
  onSubmit,
}: StepProps & {
  destination?: string;
  onSubmit: (code: string, password: string) => void;
}) => {
  const { t } = useTranslation();
  const passwordsMatch = usePasswordsMatch();
  return (
    <StepForm
      description={t(
        'Enter the code we sent to {{destination}} and choose a new password.',
        { destination: destination ?? t('your email') }
      )}
      submitLabel={t('Reset password')}
      busy={busy}
      validate={passwordsMatch}
      onSubmit={(data) =>
        onSubmit(
          text(data, 'code').replace(/\s/g, ''),
          String(data.get('password'))
        )
      }
      onBack={onBack}
    >
      <CodeField label={t('Code')} />
      <NewPasswordFields />
    </StepForm>
  );
};
